<template>

    <Head :title="__('Select Module')" />

    <div class="min-h-screen flex flex-col lg:flex-row bg-white">
        <div class="relative hidden lg:flex lg:flex-1">
            <img :src="pageSplash"
                 alt="Module Selection Splash"
                 class="h-full w-full object-cover" />

            <div class="absolute top-6 left-8 flex items-center gap-3 text-white">
                <img :src="loginLogo"
                     alt="Logo"
                     class="h-12 w-auto" />
            </div>
        </div>

        <div class="flex flex-col w-full lg:max-w-4xl bg-white min-h-screen lg:min-h-auto">
            <div class="flex lg:hidden items-center justify-center pt-8">
                <img :src="loginLogo"
                     alt="Logo"
                     class="h-10 w-auto" />
            </div>

            <div class="flex-1 flex items-center justify-center px-6 py-10 lg:px-12">
                <div class="w-full max-w-4xl">
                    <div class="mb-8 text-center">
                        <h1 class="text-2xl font-semibold text-gray-900">Wybierz moduł</h1>
                        <p class="mt-2 text-sm text-gray-500">Wybierz moduł, z którego chcesz skorzystać</p>
                        <div v-if="warning"
                             class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p class="text-sm text-yellow-800">
                                <i class="pi pi-exclamation-triangle mr-2"></i>
                                Brak uprawnień do modułu lub wybrany moduł nie istnieje!
                            </p>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Link v-for="(module, symbol) in modules"
                              :key="symbol"
                              :href="module.route ? checkRoute(module.route, null, __('Selected module not exists')) : checkRoute('set-module', symbol, __('Selected module not exists'))"
                              class="group relative flex flex-col p-6 bg-white border rounded-lg hover:shadow-lg transition-all duration-200 cursor-pointer"
                              :class="symbol == currentModule ? 'border-secondary-600 bg-secondary-50 ring-2 ring-secondary-600' : 'border-gray-200 hover:border-secondary-300'">
                            <div class="flex items-start gap-4">
                                <div class="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-colors"
                                     :class="symbol == currentModule ? 'bg-secondary-600 text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-secondary-100 group-hover:text-secondary-600'">
                                    <i :class="getModuleIcon(symbol)"
                                       class="text-xl"></i>
                                </div>

                                <div class="flex-1 min-w-0">
                                    <h3 class="text-base font-semibold text-gray-900 mb-1">
                                        {{ module.title }}
                                    </h3>
                                    <p v-if="module.subtitle"
                                       class="text-sm text-gray-600 mb-2">
                                        {{ module.subtitle }}
                                    </p>
                                    <p class="text-gray-500 line-clamp-2"
                                       style="font-size:11px">
                                        {{ module.description }}
                                    </p>
                                </div>
                            </div>

                            <div v-if="symbol == currentModule"
                                 class="absolute top-3 right-3">
                                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-600 text-white">
                                    <i class="pi pi-check mr-1 text-xs"></i>
                                    Aktywny
                                </span>
                            </div>
                            <div class="mt-4">
                                <span class="inline-flex items-center justify-center w-full px-4 py-2 rounded-md text-sm font-medium transition-colors"
                                      :class="symbol == currentModule ? 'bg-secondary-600 text-white' : 'bg-secondary-500 text-white group-hover:bg-secondary-700'">
                                    {{ symbol == currentModule ? 'Aktualnie wybrany' : 'Wybierz moduł' }}
                                    <i class="pi pi-arrow-right ml-2 text-xs"></i>
                                </span>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            <footer class="px-4 py-4 text-sm text-gray-500">
                <div class="text-center">
                    <p class="font-light">Powered by © 4Evolve wersja 1.0</p>
                    <p class="text-xs text-gray-400 mt-1">rewizja {{ systemRevision }}</p>
                </div>
            </footer>
        </div>
    </div>
</template>

<script setup>
import { usePage, Head } from '@inertiajs/vue3';
import { ref, onBeforeMount } from 'vue';
import { Link } from '@inertiajs/vue3';
import { checkRoute, __ } from '@helper/utils.js';
import Layout from '@layouts/LayoutNoMenuNoFooter.vue';
import { loginLogo, pageSplash } from '@helper/constants.js';

const page = usePage();

defineOptions({ layout: Layout });

const props = defineProps({
    currentModule: String,
    warning: { type: Boolean, default: false },
    modules: { type: Object, default: () => ({}) },
});
const modules = ref([]);

const systemRevision = page.props.systemRevision;

//------------------------------------------------------------------

onBeforeMount(async () => {
    if (props.warning) {
        console.warn('Brak uprawnień do modułu lub wybrany moduł nie istnieje!');
    }
    try {
        modules.value = props.modules;
    } catch (error) {
        console.error(error);
    }
});

//------------------------------------------------------------------

const getModuleIcon = (symbol) => {
    return modules.value[symbol.toLowerCase()]?.icon || '';
};
</script>

//------------------------------------------------------------------

<style scoped></style>
